import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { DropDownHintMsg, DropDownField, DropDownMenu } from "../../index";
import './dropdown.scss';

export const DropDownDefault = React.forwardRef(({
    autoFocus = false,
    colorStyle = 'neutral',
    disabled = false,
    hintMsg,
    id,
    label,
    name,
    onBlur = () => { },
    onChange = () => { },
    options,
    required = false,
    value,
    ...inputProps
}, ref) => {

    const dropdownRef = ref;

    let selectedOption = options && options.find(opt => opt.value == value)
    let indexFound = options && options.findIndex(opt => opt.value == value);
    const [selectedLabel, setSelectedLabel] = React.useState(selectedOption && selectedOption.label || '');
    const [selectedIndex, setSelectedIndex] = React.useState(typeof indexFound === 'number' && indexFound !== -1 ? (required ? indexFound.toString() : (indexFound + 1).toString()) : '');
    const [showOptions, setShowOptions] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(value);

    useEffect(() => {
        setHasValueStyle();
        // if dropdown value is changed externally need to update selected label
        if (selectedLabel === '' && value) {
            let selectedVal = options && options.find(opt => opt.value == value)
            let selectedIndex = options && options.findIndex(opt => opt.value == value);
            if (selectedVal && selectedVal?.label) {
                setSelectedLabel(selectedVal?.label);
            }
            if (typeof selectedIndex === 'number' && selectedIndex !== -1) {
                setSelectedIndex(required ? indexFound.toString() : (indexFound + 1).toString());
            }
        }
    }, [value]);

    const handleChange = (e, value, label, index) => {
        setHasValueStyle();
        setSelectedLabel(label);
        setSelectedIndex(index.toString());

        let event = {
            ...e, target: {
                value: value,
                name: dropdownRef.current.name ? dropdownRef.current.name : ""
            }
        }
        onChange(event);
        setShowOptions(false);

        let eventBlur = {
            ...event, type: 'blur'
        }

        onBlur(eventBlur);
    }

    const toggleOptions = (e) => {
        setShowOptions(!showOptions);
    }

    const handleBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowOptions(false);

            let evt = {
                ...event, target: {
                    value: value,
                    name: dropdownRef.current.name ? dropdownRef.current.name : ""
                }
            }

            if (onBlur) {
                onBlur(evt);
            }
        }
    }

    // Event handler for keydowns
    const handleInputKeyDown = (e) => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault();
                toggleOptions();
                break;
            default:
                break;
        }
    };

    // Event handler for keydowns
    const handleOptionsKeyDown = (e, value, label, index) => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault();
                e.persist();
                handleChange(e, value, label, index);
                break;
            case "Escape":
                e.preventDefault();
                setShowOptions(false);
            default:
                break;
        }
    };

    const setHasValueStyle = () => {
        if (dropdownRef.current.value && dropdownRef.current.value?.length > 0) {
            setHasValue(true);
        } else {
            setHasValue(false);
        }
    }

    const dropdownClasses = [
        `bsw-dropdown`,
        `bsw-dropdown-color--${colorStyle}`,
        `${disabled ? 'bsw-dropdown--disabled' : ''}`,
        `${hasValue ? 'bsw-dropdown--filled' : ''}`,
    ].join(' ');

    const selectedId = selectedIndex ? `${id}-option-${selectedIndex}` : '';

    return (
        <div className={dropdownClasses} onBlur={handleBlur}>
            <DropDownField
                {...inputProps}
                autoFocus={autoFocus}
                disabled={disabled}
                id={id}
                label={label}
                name={name}
                onClick={toggleOptions}
                onKeyDown={handleInputKeyDown}
                ref={dropdownRef}
                required={required}
                selectedId={selectedId}
                selectedLabel={selectedLabel}
                showOptions={showOptions}
            />
            {(options && showOptions && !disabled) &&
                <DropDownMenu
                    id={id}
                    onClick={handleChange}
                    onKeyDown={handleOptionsKeyDown}
                    options={options}
                    required={required}
                    selectedId={selectedId}
                    selectedIndex={selectedIndex}
                />
            }
            {hintMsg &&
                <DropDownHintMsg
                    colorStyle={colorStyle}
                    hintMsg={hintMsg}
                />
            }
        </div>
    )
})

DropDownDefault.propTypes = {
    autoFocus: PropTypes.bool,
    colorStyle: PropTypes.string,
    disabled: PropTypes.bool,
    hintMsg: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    options: PropTypes.array,
    required: PropTypes.bool,
    value: PropTypes.string,
}
